import type { GaugeVisualState } from '@atiautomacao/ati-ui-library';
import { truncateNumber } from '../../../../../../Utils/NumberUtil';

export type { GaugeVisualState };

export interface StringCurrentPoint {
    teleObjectId: number;
    name: string;
    dateTime: string | Date;
    value: number | null;
}

export interface StringCurrentGaugeView {
    teleObjectId: number;
    name: string;
    currentAmps: number | null;
    fillRatio: number;
    visualState: GaugeVisualState;
}

export interface GaugeStateFlags {
    /** teleObjectId → stale */
    staleByTeleObjectId?: ReadonlySet<number> | ReadonlyMap<number, boolean>;
    /** teleObjectId → alarm eligible */
    alarmByTeleObjectId?: ReadonlySet<number> | ReadonlyMap<number, boolean>;
}

function isValidDateTime(value: unknown): value is string | Date {
    if (value instanceof Date) {
        return !Number.isNaN(value.getTime());
    }
    return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}

export function isStringCurrentPoint(value: unknown): value is StringCurrentPoint {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const candidate = value as Partial<Record<keyof StringCurrentPoint, unknown>>;
    const hasValidValue =
        candidate.value === null ||
        (typeof candidate.value === 'number' && Number.isFinite(candidate.value));

    return (
        typeof candidate.teleObjectId === 'number' &&
        Number.isFinite(candidate.teleObjectId) &&
        typeof candidate.name === 'string' &&
        isValidDateTime(candidate.dateTime) &&
        hasValidValue
    );
}

export function parseStringCurrentPoints(payload: unknown): StringCurrentPoint[] {
    return Array.isArray(payload) ? payload.filter(isStringCurrentPoint) : [];
}

export function isMaxCurrentConfigured(
    maxStringCurrent: number | null | undefined
): maxStringCurrent is number {
    return (
        typeof maxStringCurrent === 'number' &&
        Number.isFinite(maxStringCurrent) &&
        maxStringCurrent > 0
    );
}

export function fillRatio(
    currentAmps: number | null | undefined,
    maxStringCurrent: number | null | undefined
): number {
    if (!isMaxCurrentConfigured(maxStringCurrent)) {
        return 0;
    }
    if (currentAmps === null || currentAmps === undefined || Number.isNaN(currentAmps)) {
        return 0;
    }
    const ratio = currentAmps / maxStringCurrent;
    return Math.min(1, Math.max(0, ratio));
}

export function latestByTeleObject(points: StringCurrentPoint[]): StringCurrentPoint[] {
    if (!points || points.length === 0) {
        return [];
    }

    const byId = new Map<number, StringCurrentPoint>();

    for (const point of points) {
        if (point.teleObjectId == null) {
            continue;
        }
        const existing = byId.get(point.teleObjectId);
        if (!existing) {
            byId.set(point.teleObjectId, point);
            continue;
        }
        const existingTime = new Date(existing.dateTime).getTime();
        const pointTime = new Date(point.dateTime).getTime();
        if (pointTime >= existingTime) {
            byId.set(point.teleObjectId, point);
        }
    }

    return Array.from(byId.values()).filter((point) => {
        const name = point.name?.trim();
        return Boolean(name);
    });
}

/** Stale takes precedence when both apply. */
export function resolveVisualState(flags: { stale?: boolean; alarm?: boolean }): GaugeVisualState {
    if (flags.stale) {
        return 'stale';
    }
    if (flags.alarm) {
        return 'alarm';
    }
    return 'normal';
}

function flagForTeleObject(
    source: ReadonlySet<number> | ReadonlyMap<number, boolean> | undefined,
    teleObjectId: number
): boolean {
    if (!source) {
        return false;
    }
    if (source instanceof Map) {
        return Boolean(source.get(teleObjectId));
    }
    return source.has(teleObjectId);
}

export function maxCurrentLabel(maxStringCurrent: number | null | undefined): string {
    if (!isMaxCurrentConfigured(maxStringCurrent)) {
        return 'Não configurado';
    }
    const formattedValue = truncateNumber(maxStringCurrent, 1).toLocaleString('pt-BR', {
        maximumFractionDigits: 1,
    });
    return `${formattedValue} A`;
}

export function buildSectionSummary(
    monitoredCount: number,
    maxStringCurrent: number | null | undefined
): string {
    return `${monitoredCount} Strings monitoradas | Limite Máximo: ${maxCurrentLabel(maxStringCurrent)}`;
}

/**
 * @param stateFlags Per-teleobject stale/alarm flags. When omitted, all gauges are `normal`
 *   (backward compatible with the previous single visualState argument).
 * @param legacyVisualState Optional uniform state when stateFlags is not provided.
 */
export function buildGaugeViews(
    points: StringCurrentPoint[],
    maxStringCurrent: number | null | undefined,
    stateFlagsOrLegacyState?: GaugeStateFlags | GaugeVisualState,
    legacyVisualState?: GaugeVisualState
): StringCurrentGaugeView[] {
    let stateFlags: GaugeStateFlags | undefined;
    let uniformState: GaugeVisualState | undefined;

    if (typeof stateFlagsOrLegacyState === 'string') {
        uniformState = stateFlagsOrLegacyState;
    } else if (stateFlagsOrLegacyState && typeof stateFlagsOrLegacyState === 'object') {
        stateFlags = stateFlagsOrLegacyState;
    }
    if (legacyVisualState) {
        uniformState = legacyVisualState;
    }

    const latest = latestByTeleObject(points);
    return latest
        .map((point) => {
            const stale = stateFlags
                ? flagForTeleObject(stateFlags.staleByTeleObjectId, point.teleObjectId)
                : false;
            const alarm = stateFlags
                ? flagForTeleObject(stateFlags.alarmByTeleObjectId, point.teleObjectId)
                : false;
            const state = stateFlags
                ? resolveVisualState({ stale, alarm })
                : uniformState ?? 'normal';

            const rawAmps =
                point.value === null || point.value === undefined || Number.isNaN(Number(point.value))
                    ? null
                    : Number(point.value);
            const currentAmps = state === 'stale' ? null : rawAmps;
            const ratio = state === 'stale' ? 0 : fillRatio(currentAmps, maxStringCurrent);

            return {
                teleObjectId: point.teleObjectId,
                name: point.name.trim(),
                currentAmps,
                fillRatio: ratio,
                visualState: state,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}
